import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAdminUsers, useCreateUser, useRestoreUser, useEmployeeTypes, useUpdateUser } from '../../hooks/useAdmin';
import { useAuth } from '../../services/authContext';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ArchiveUserModal } from '../../components/admin/ArchiveUserModal';
import { formatCurrency } from '../../utils/format';
import { availableRoles, normalizeRole } from '../../utils/roles';
import { Modal } from '../../components/common/Modal';
import type { AdminUser, CreateUserInput, ClaudeTierChoice } from '../../services/api/admin';

type SortKey = 'name' | 'email' | 'github' | 'hourlyRate' | 'status';
type SortDir = 'asc' | 'desc';

export default function UserManagementPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [showDeleted, setShowDeleted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [archiveUser, setArchiveUser] = useState<AdminUser | null>(null);
  // FBV-01: restore-modal state — mirrors the archive-modal pattern in this file.
  const [restoreTarget, setRestoreTarget] = useState<AdminUser | null>(null);
  const [restoreSkipOnboarding, setRestoreSkipOnboarding] = useState(false);
  const [restoreTier, setRestoreTier] = useState<ClaudeTierChoice>('claude-standard');
  const { data: users, isLoading } = useAdminUsers(showDeleted);
  const { data: employeeTypes } = useEmployeeTypes();
  const createMutation = useCreateUser();
  const restoreMutation = useRestoreUser();
  const updateMutation = useUpdateUser();

  const [editingGithubId, setEditingGithubId] = useState<number | null>(null);
  const [editingGithubValue, setEditingGithubValue] = useState('');

  const startEditGithub = (id: number, current: string | null) => {
    setEditingGithubId(id);
    setEditingGithubValue(current ?? '');
  };

  const cancelEditGithub = () => {
    setEditingGithubId(null);
    setEditingGithubValue('');
  };

  const saveEditGithub = async (id: number, original: string | null) => {
    const trimmed = editingGithubValue.trim();
    const nextValue = trimmed === '' ? null : trimmed;
    if (nextValue === (original ?? null)) {
      cancelEditGithub();
      return;
    }
    try {
      await updateMutation.mutateAsync({ id, input: { githubUsername: nextValue } });
    } finally {
      cancelEditGithub();
    }
  };

  const [nameFilter, setNameFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'name', dir: 'asc' });

  const [form, setForm] = useState<CreateUserInput>({ email: '' });
  const [formError, setFormError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      const created = await createMutation.mutateAsync(form);
      setShowForm(false);
      setForm({ email: '' });
      navigate(`/admin/users/${created.id}?section=workflows`);
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const toggleSort = (key: SortKey) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  const filteredUsers = useMemo(() => {
    let list = [...(users || [])];

    if (nameFilter) {
      const q = nameFilter.toLowerCase();
      list = list.filter((u) => {
        const name = `${u.firstName || ''} ${u.lastName || ''} ${u.email}`.toLowerCase();
        return name.includes(q);
      });
    }

    if (roleFilter) {
      list = list.filter((u) => u.roles.some((r) => normalizeRole(r) === roleFilter));
    }

    if (typeFilter) {
      const typeId = Number(typeFilter);
      list = list.filter((u) => u.employeeTypeId === typeId);
    }

    const mult = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sort.key) {
        case 'name': {
          const nameA = `${a.lastName || ''}, ${a.firstName || ''}`.toLowerCase();
          const nameB = `${b.lastName || ''}, ${b.firstName || ''}`.toLowerCase();
          return mult * nameA.localeCompare(nameB);
        }
        case 'email':
          return mult * a.email.localeCompare(b.email);
        case 'github': {
          // Nulls/empties sort to the end in asc, start in desc
          const ga = a.githubUsername || '';
          const gb = b.githubUsername || '';
          if (!ga && !gb) return 0;
          if (!ga) return 1;
          if (!gb) return -1;
          return mult * ga.localeCompare(gb);
        }
        case 'hourlyRate':
          return mult * ((a.currentHourlyCosts ?? 0) - (b.currentHourlyCosts ?? 0));
        case 'status': {
          const statusA = a.deletedAt ? 1 : 0;
          const statusB = b.deletedAt ? 1 : 0;
          return mult * (statusA - statusB);
        }
        default:
          return 0;
      }
    });

    return list;
  }, [users, nameFilter, roleFilter, typeFilter, sort]);

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/admin')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Admin
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Users <span className="text-lg font-normal text-gray-500">({filteredUsers.length})</span></h1>
          <div className="flex gap-2">
            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show deleted
            </label>
            <Button size="sm" onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Cancel' : 'Add User'}
            </Button>
          </div>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="Email *"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={form.firstName || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value || undefined }))}
              placeholder="First Name"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={form.lastName || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value || undefined }))}
              placeholder="Last Name"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="email"
              value={form.personalEmail || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, personalEmail: e.target.value || undefined }))}
              placeholder="Personal email"
              required
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={form.githubUsername || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, githubUsername: e.target.value || undefined }))}
              placeholder="GitHub username"
              required
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            {/* Phase 84 follow-up — onboarding buddy: who looks after this
                person when they join. Optional; deleted users are excluded. */}
            <select
              value={form.buddyId ?? ''}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  buddyId: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
            >
              <option value="">Buddy (optional)</option>
              {(users ?? [])
                .filter((u) => !u.deletedAt)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {[u.lastName, u.firstName].filter(Boolean).join(' ') || u.email}
                  </option>
                ))}
            </select>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.skipOnboarding || false}
                  onChange={(e) => setForm((prev) => ({ ...prev, skipOnboarding: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                Skip onboarding workflow
              </label>
              {/* FBV-01: Claude tier selector — admin picks Standard or Premium at user creation */}
              <label className="flex items-center gap-2 text-sm text-gray-700">
                Claude tier
                <select
                  value={form.claudeTier ?? 'claude-standard'}
                  onChange={(e) => setForm((prev) => ({ ...prev, claudeTier: e.target.value as ClaudeTierChoice }))}
                  className="rounded-lg border border-gray-300 px-2 py-1 text-sm"
                >
                  <option value="claude-standard">Claude: Standard</option>
                  <option value="claude-premium">Claude: Premium</option>
                <option value="none">Bez Claude licence</option>
                </select>
              </label>
            </div>
            <Button type="submit" size="sm" isLoading={createMutation.isPending}>Create</Button>
          </div>
        </form>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          placeholder="Filter by name or email..."
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-64"
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">All roles</option>
          {/*
            Phase 27 D-17 + Pitfall #6: render options from `availableRoles`
            (single source of truth in `frontend/src/utils/roles.ts`). When new
            roles are added to that array (e.g. D-04 added client_manager,
            finance, hr, marketing, isms_manager) the dropdown updates with
            zero changes here. NEVER hardcode option values in this list.
          */}
          {availableRoles.map((role) => (
            <option key={role.value} value={role.value}>{role.label}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">All employee types</option>
          {employeeTypes?.map((type) => (
            <option key={type.id} value={type.id}>{type.name}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader label="Name" sortKey="name" current={sort} onSort={toggleSort} />
              <SortableHeader label="Email" sortKey="email" current={sort} onSort={toggleSort} />
              <SortableHeader label="GitHub" sortKey="github" current={sort} onSort={toggleSort} />
              <SortableHeader label="Hourly Rate" sortKey="hourlyRate" current={sort} onSort={toggleSort} align="right" />
              <SortableHeader label="Status" sortKey="status" current={sort} onSort={toggleSort} />
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">LLM Condom</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <tr key={user.id} className={user.deletedAt ? 'bg-red-50/50' : 'hover:bg-gray-50'}>
                <td className="px-6 py-3 text-sm">
                  <Link
                    to={`/admin/users/${user.id}`}
                    className="text-primary-600 hover:underline font-medium"
                  >
                    {user.lastName && user.firstName
                      ? `${user.lastName}, ${user.firstName}`
                      : user.lastName || user.firstName || '-'}
                  </Link>
                </td>
                <td className="px-6 py-3 text-sm text-gray-600">{user.email}</td>
                <td className="px-6 py-3 text-sm text-gray-600">
                  {editingGithubId === user.id ? (
                    <input
                      type="text"
                      autoFocus
                      value={editingGithubValue}
                      onChange={(e) => setEditingGithubValue(e.target.value)}
                      onBlur={() => saveEditGithub(user.id, user.githubUsername)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          (e.currentTarget as HTMLInputElement).blur();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelEditGithub();
                        }
                      }}
                      disabled={updateMutation.isPending}
                      placeholder="GitHub username"
                      className="w-40 rounded border border-gray-300 px-2 py-1 text-sm"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => !user.deletedAt && startEditGithub(user.id, user.githubUsername)}
                      disabled={!!user.deletedAt}
                      className={`text-left ${user.deletedAt ? 'cursor-not-allowed' : 'hover:text-gray-900 hover:underline decoration-dotted'}`}
                    >
                      {user.githubUsername || <span className="text-gray-400">-</span>}
                    </button>
                  )}
                </td>
                <td className="px-6 py-3 text-sm text-gray-900 text-right">
                  {user.currentHourlyCosts != null ? formatCurrency(user.currentHourlyCosts) : '-'}
                </td>
                <td className="px-6 py-3 text-sm">
                  {user.deletedAt ? (
                    <span className="text-red-600">Deleted</span>
                  ) : (
                    <span className="text-green-600">Active</span>
                  )}
                </td>
                <td className="px-6 py-3 text-sm">
                  {user.hasLLMCondom ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                      On
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                      Off
                    </span>
                  )}
                </td>
                <td className="px-6 py-3 text-sm text-right space-x-3">
                  {user.deletedAt ? (
                    // FBV-01: open restore modal to let admin pick a Claude tier.
                    <button
                      onClick={() => { setRestoreTier('claude-standard'); setRestoreSkipOnboarding(false); setRestoreTarget(user); }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Restore
                    </button>
                  ) : (
                    <button
                      onClick={() => setArchiveUser(user)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Archive
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ArchiveUserModal
        isOpen={archiveUser !== null}
        onClose={() => setArchiveUser(null)}
        user={archiveUser}
        defaultDriveTransferTo={currentUser?.email ?? ''}
      />

      {/* FBV-01: Restore modal with Claude tier selector — mirrors archive-modal pattern */}
      <Modal
        isOpen={restoreTarget !== null}
        onClose={() => setRestoreTarget(null)}
        title="Restore User"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setRestoreTarget(null)} disabled={restoreMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isLoading={restoreMutation.isPending}
              onClick={() => {
                if (!restoreTarget) return;
                restoreMutation.mutate({ id: restoreTarget.id, claudeTier: restoreTier, skipOnboarding: restoreSkipOnboarding });
                setRestoreTarget(null);
              }}
            >
              Restore
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Restoring{' '}
            <span className="font-medium">
              {restoreTarget?.lastName && restoreTarget?.firstName
                ? `${restoreTarget.lastName}, ${restoreTarget.firstName}`
                : restoreTarget?.email}
            </span>
            . Choose the Claude access tier for the onboarding workflow.
          </p>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={restoreSkipOnboarding}
              onChange={(e) => setRestoreSkipOnboarding(e.target.checked)}
              className="rounded border-gray-300"
            />
            Skip onboarding workflow
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Claude tier
            <select
              value={restoreTier}
              onChange={(e) => setRestoreTier(e.target.value as ClaudeTierChoice)}
              className="rounded-lg border border-gray-300 px-2 py-1 text-sm"
            >
              <option value="claude-standard">Claude: Standard</option>
              <option value="claude-premium">Claude: Premium</option>
                <option value="none">Bez Claude licence</option>
            </select>
          </label>
        </div>
      </Modal>
    </div>
  );
}

function SortableHeader({ label, sortKey, current, onSort, align }: {
  label: string;
  sortKey: SortKey;
  current: { key: SortKey; dir: SortDir };
  onSort: (key: SortKey) => void;
  align?: 'right';
}) {
  const active = current.key === sortKey;
  return (
    <th
      className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active && <span className="ml-1">{current.dir === 'asc' ? '\u2191' : '\u2193'}</span>}
    </th>
  );
}
